import { Component, OnInit } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-insurance-domiciliation-simulator',
  templateUrl: './insurance-domiciliation-simulator.component.html',
  styleUrls: ['./insurance-domiciliation-simulator.component.css']
})
export class InsuranceDomiciliationSimulatorComponent implements OnInit {
  
  // Datos del cliente
  customerIdentification: string = '';
  customerNationality: string = 'V'; // V, E, J, G, P
  customerData: any = null;
  isLoadingCustomer: boolean = false;
  
  // Opciones de nacionalidad
  nationalities = [
    { code: 'V', name: 'V - Venezolano' },
    { code: 'E', name: 'E - Extranjero' },
    { code: 'J', name: 'J - Jurídico' },
    { code: 'G', name: 'G - Gubernamental' },
    { code: 'P', name: 'P - Pasaporte' }
  ];
  
  // Estado del flujo
  currentStep: 'search' | 'validation' | 'pending_tac' | 'register_payment' | 'completed' = 'search';
  validationResult: any = null;
  pendingTAC: any = null;
  isLoadingValidation: boolean = false;
  isLoadingTAC: boolean = false;
  
  // Configuración
  productType: string = 'SEGUROS_MERCANTIL';
  
  constructor(
    private _user: UserService
  ) { }

  ngOnInit(): void {
    console.log('🎯 Simulador de Domiciliación de Seguros iniciado');
  }

  /**
   * Buscar cliente por cédula
   */
  async searchCustomer() {
    if (!this.customerIdentification || this.customerIdentification.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Debe ingresar una cédula'
      });
      return;
    }

    this.isLoadingCustomer = true;
    console.log('🔍 Buscando cliente con cédula:', this.customerIdentification);

    try {
      // Usar el mismo método que search-client.component.ts
      // Formato: { type_identity: 'CI', code_identity: 'V', identity: '12345678' }
      const body = {
        type_identity: 'CI',
        code_identity: this.customerNationality,
        identity: this.customerIdentification
      };
      
      const response: any = await this._user.getPersonByIdentifier(body);
      
      console.log('📦 Respuesta completa del backend:', response);
      
      // La respuesta viene en response.resp.person
      if (response && response.resp && response.resp.person) {
        this.customerData = response.resp.person;
        console.log('✅ Cliente encontrado:', this.customerData);
        
        Swal.fire({
          icon: 'success',
          title: 'Cliente Encontrado',
          html: `
            <p><strong>Nombre:</strong> ${this.customerData.name} ${this.customerData.lastName}</p>
            <p><strong>Cédula:</strong> ${response.resp.identity}</p>
            <p><strong>Code:</strong> ${this.customerData.code}</p>
          `,
          confirmButtonText: 'Continuar'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Cliente No Encontrado',
          text: 'No se encontró un cliente con esa cédula'
        });
        this.customerData = null;
      }
    } catch (error) {
      console.error('❌ Error buscando cliente:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al buscar el cliente'
      });
      this.customerData = null;
    } finally {
      this.isLoadingCustomer = false;
    }
  }

  /**
   * Simular compra de seguro (validar domiciliación)
   */
  async simulateInsurancePurchase() {
    if (!this.customerData) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Debe buscar un cliente primero'
      });
      return;
    }

    this.isLoadingValidation = true;
    this.currentStep = 'validation';
    console.log('🏥 Validando domiciliación para:', this.customerData.code);

    try {
      const response: any = await this._user.validateSubscriptionDomiciliation(
        this.customerData.code,
        this.productType
      );

      this.validationResult = response;
      console.log('📋 Resultado de validación:', response);

      // Analizar resultado según los 4 casos
      this.handleValidationResult(response);

    } catch (error) {
      console.error('❌ Error validando domiciliación:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al validar la domiciliación'
      });
      this.currentStep = 'search';
    } finally {
      this.isLoadingValidation = false;
    }
  }

  /**
   * Manejar resultado de validación según los 4 casos
   */
  private handleValidationResult(result: any) {
    console.log('🔍 Analizando resultado de validación...');

    // CASO 1: Cliente ya domiciliado ✅
    if (result.domiciliated === true) {
      console.log('✅ CASO 1: Cliente ya domiciliado');
      this.currentStep = 'completed';
      
      Swal.fire({
        icon: 'success',
        title: '¡Cliente Domiciliado!',
        html: `
          <p>El cliente ya tiene domiciliación activa para <strong>${this.productType}</strong></p>
          <p>✅ Puede proceder con la compra del seguro sin acciones adicionales</p>
        `,
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // CASO 2A-BIS: Tiene método de pago y T&C aceptados pero afiliación PENDING ⏳
    if (result.has_payment_method_registered === true && 
        result.has_terms_accepted === true && 
        result.afiliation_status === 'PENDING') {
      console.log('⏳ CASO 2A-BIS: Afiliación pendiente de aprobación bancaria');
      this.currentStep = 'pending_tac';
      
      const afiliationDetails = result.afiliation_details || [];
      const afiliationInfo = afiliationDetails.map((aff: any) => 
        `<li>${aff.bank} - Estado: ${aff.status} (${new Date(aff.created_at).toLocaleDateString()})</li>`
      ).join('');
      
      Swal.fire({
        icon: 'info',
        title: 'Afiliación en Proceso',
        html: `
          <p>El cliente tiene método de pago y T&C aceptados, pero la afiliación bancaria está <strong>pendiente de aprobación</strong></p>
          <div style="text-align: left; margin-top: 15px;">
            <p><strong>Afiliaciones pendientes:</strong></p>
            <ul>${afiliationInfo}</ul>
          </div>
          <hr>
          <p class="text-muted"><strong>Próximos pasos:</strong></p>
          <ul style="text-align: left; font-size: 0.9em;">
            ${result.next_steps?.map((step: string) => `<li>${step}</li>`).join('') || ''}
          </ul>
          <hr>
          <div style="background-color: #d1ecf1; padding: 15px; border-left: 4px solid #0c5460; border-radius: 5px; margin-top: 15px;">
            <p style="margin: 0; font-size: 0.95em; color: #0c5460;">
              <i class="fas fa-info-circle"></i> 
              <strong>Importante:</strong> A pesar de que la afiliación está pendiente, 
              <strong>el cliente puede continuar con la adquisición de su producto de seguro</strong>. 
              Este proceso no detendrá la contratación.
            </p>
          </div>
        `,
        confirmButtonText: 'Entendido',
        width: '650px'
      });
      return;
    }

    // CASO 2A: Tiene método de pago registrado pero sin T&C aceptados 📝
    if (result.has_payment_method_registered === true && 
        (result.has_terms_accepted === false || 
         (result.pending_terms_payment_methods && result.pending_terms_payment_methods.length > 0))) {
      console.log('📝 CASO 2A: Método registrado sin T&C');
      console.log('📋 Métodos de pago con T&C pendientes:', result.pending_terms_payment_methods);
      
      this.currentStep = 'pending_tac';
      
      // Si viene pending_terms_payment_methods, usarlo directamente
      if (result.pending_terms_payment_methods && result.pending_terms_payment_methods.length > 0) {
        const pendingMethods = result.pending_terms_payment_methods;
        const methodInfo = pendingMethods.map((pm: any) => 
          `<li>${pm.bank} - Cuenta: ${pm.account_number}</li>`
        ).join('');
        
        Swal.fire({
          icon: 'info',
          title: 'Términos y Condiciones Pendientes',
          html: `
            <p>El cliente tiene método(s) de pago registrado(s) pero debe aceptar los Términos y Condiciones</p>
            <div style="text-align: left; margin: 15px 0;">
              <p><strong>Métodos de pago pendientes:</strong></p>
              <ul>${methodInfo}</ul>
            </div>
            <p>Se procederá a mostrar los T&C para aceptación</p>
          `,
          confirmButtonText: 'Ver T&C'
        }).then(() => {
          // Cargar T&C directamente desde pending_terms_payment_methods
          this.showTACModalFromPendingMethods(pendingMethods);
        });
      } else {
        // Flujo original: cargar T&C desde el servicio
        Swal.fire({
          icon: 'info',
          title: 'Términos y Condiciones Pendientes',
          html: `
            <p>El cliente tiene un método de pago registrado pero debe aceptar los Términos y Condiciones</p>
            <p>Se procederá a mostrar los T&C pendientes</p>
          `,
          confirmButtonText: 'Ver T&C'
        }).then(() => {
          this.loadPendingTAC();
        });
      }
      return;
    }

    // CASO 2B: Tiene cuentas copiables de otros productos 🔄
    if (result.other_domiciliations && result.other_domiciliations.length > 0) {
      console.log('🔄 CASO 2B: Cuentas copiables disponibles');
      
      // Tomar la primera cuenta disponible
      const firstAccount = result.other_domiciliations[0];
      
      // Mostrar modal de autorización de cargo
      this.showCopyAccountAuthorization(firstAccount, result.other_domiciliations);
      return;
    }

    // CASO 3: Sin domiciliación - Debe registrar método de pago ❌
    if (result.has_payment_method_registered === false) {
      console.log('❌ CASO 3: Sin métodos de pago registrados');
      console.log('🏦 Bancos compatibles:', result.compatible_banks);
      this.currentStep = 'register_payment';
      
      // Crear lista de bancos compatibles
      const banksList = result.compatible_banks?.map((bank: any) => 
        `<li><strong>${bank.bank_name}</strong></li>`
      ).join('') || '<li>No hay bancos disponibles</li>';

      Swal.fire({
        title: 'Cargo Automático',
        html: `
          <div style="text-align: left; padding: 20px;">
            <p style="font-size: 1.05em; margin-bottom: 20px; line-height: 1.6;">
              ¿Si posees cuenta en alguno de estos bancos?
            </p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
              <ul style="margin: 0; padding-left: 20px;">
                ${banksList}
              </ul>
            </div>
            
            <p style="font-size: 0.95em; color: #495057; line-height: 1.6; margin-bottom: 15px;">
              Te recomendamos registrar tu cuenta, aceptar términos y condiciones y nosotros nos encargamos del resto.
            </p>
            
            <div style="background-color: #d4edda; padding: 15px; border-left: 4px solid #28a745; border-radius: 5px; margin-bottom: 15px;">
              <p style="margin: 0; font-size: 0.9em; color: #155724; line-height: 1.5;">
                <i class="fas fa-check-circle" style="color: #28a745;"></i> Registro rápido<br>
                <i class="fas fa-check-circle" style="color: #28a745;"></i> Aceptación de T&C<br>
                <i class="fas fa-check-circle" style="color: #28a745;"></i> Cobros automáticos<br>
                <i class="fas fa-check-circle" style="color: #28a745;"></i> Sin preocupaciones
              </p>
            </div>
            
            <p style="font-size: 0.9em; color: #6c757d; font-style: italic;">
              <i class="fas fa-info-circle"></i> Tú solo te encargas de tener el dinero en la cuenta en las fechas pautadas.
            </p>
          </div>
        `,
        icon: 'question',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: '<i class="fas fa-check"></i> Sí, deseo hacerlo',
        denyButtonText: '<i class="fas fa-bell"></i> Recordármelo más tarde',
        cancelButtonText: '<i class="fas fa-times"></i> No',
        confirmButtonColor: '#28a745',
        denyButtonColor: '#ff9800',
        cancelButtonColor: '#6c757d',
        width: '650px',
        customClass: {
          confirmButton: 'btn btn-success btn-lg',
          denyButton: 'btn btn-warning btn-lg',
          cancelButton: 'btn btn-secondary btn-lg'
        }
      }).then((modalResult) => {
        if (modalResult.isConfirmed) {
          // Usuario acepta: mostrar modal de redirección
          Swal.fire({
            icon: 'info',
            title: 'Redirección a Registro de Métodos de Pago',
            html: `
              <p>Serás redirigido al módulo de <strong>Registro de Métodos de Pago</strong> para el producto <strong>${this.getProductName()}</strong></p>
              <p class="mt-3">Allí podrás registrar tu cuenta bancaria y completar la domiciliación.</p>
            `,
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#007bff'
          }).then(() => {
            this.redirectToPaymentMethodRegistration();
          });
        } else if (modalResult.isDenied) {
          // Usuario presiona "Recordármelo más tarde"
          this.createReminder();
        }
        // Si cancela (No), simplemente se cierra el modal sin hacer nada
      });
      return;
    }

    // Caso no contemplado
    console.warn('⚠️ Caso no contemplado:', result);
    Swal.fire({
      icon: 'warning',
      title: 'Caso No Contemplado',
      text: 'La respuesta del servidor no coincide con ningún caso esperado'
    });
  }

  /**
   * Cargar T&C pendientes (CASO 2A)
   */
  async loadPendingTAC() {
    this.isLoadingTAC = true;
    console.log('📄 Cargando T&C pendientes...');

    try {
      const response: any = await this._user.getCustomerPendingTAC(
        this.customerData.code,
        this.productType
      );

      console.log('📋 Respuesta completa de T&C pendientes:', response);

      // La respuesta viene con estructura: { success, pending_count, resp: [...], product_type }
      const tacList = response?.resp || response;
      const pendingCount = response?.pending_count || tacList?.length || 0;

      this.pendingTAC = tacList;
      console.log('📋 T&C pendientes extraídos:', tacList, 'Count:', pendingCount);

      if (tacList && tacList.length > 0 && pendingCount > 0) {
        // Mostrar modal con T&C (similar a credit-apk)
        this.showTACModal(tacList);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Sin T&C Pendientes',
          text: 'No hay términos y condiciones pendientes por aceptar'
        });
      }

    } catch (error) {
      console.error('❌ Error cargando T&C:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar los términos y condiciones'
      });
    } finally {
      this.isLoadingTAC = false;
    }
  }

  /**
   * Mostrar modal con T&C desde pending_terms_payment_methods (respuesta directa del validate)
   */
  async showTACModalFromPendingMethods(pendingMethods: any[]) {
    console.log('📄 Mostrando T&C desde pending_terms_payment_methods:', pendingMethods);

    const firstMethod = pendingMethods[0];
    const paymentMethodCustomerId = firstMethod.payment_method_customer_id;
    
    try {
      console.log('🔑 Obteniendo T&C para payment_method_customer_id:', paymentMethodCustomerId);
      
      // Obtener el HTML completo del T&C
      const tacResponse: any = await this._user.getTermsByPaymentMethod(
        paymentMethodCustomerId
      );

      console.log('📄 T&C obtenido:', tacResponse);

      const tacHTML = tacResponse?.terms?.details || '<p>No se pudo cargar el contenido de los T&C</p>';
      const termsAndConditionId = tacResponse?.terms?._id;

      if (!termsAndConditionId) {
        throw new Error('No se pudo obtener el ID de los términos y condiciones');
      }

      // Mostrar modal con el HTML de los T&C
      Swal.fire({
        title: 'Términos y Condiciones de Domiciliación',
        html: `
          <div style="max-height: 400px; overflow-y: auto; text-align: left; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
            ${tacHTML}
          </div>
        `,
        width: '800px',
        showCancelButton: true,
        confirmButtonText: 'Aceptar T&C',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#dc3545'
      }).then((result) => {
        if (result.isConfirmed) {
          // Aceptar T&C con el formato correcto
          this.acceptTACSimple(paymentMethodCustomerId, termsAndConditionId);
        }
      });

    } catch (error) {
      console.error('❌ Error obteniendo T&C:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar el contenido de los T&C'
      });
    }
  }

  /**
   * Mostrar modal con T&C (similar a domiciliation-modal en credit-apk)
   */
  async showTACModal(tacList: any[]) {
    console.log('📄 Mostrando modal de T&C:', tacList);

    // Obtener el HTML del T&C
    const firstTAC = tacList[0];
    
    try {
      // El ID del payment_method_customer viene en firstTAC._id
      const paymentMethodCustomerId = firstTAC._id;
      console.log('🔑 Obteniendo T&C para payment_method_customer_id:', paymentMethodCustomerId);
      
      // Obtener el HTML completo del T&C
      const tacResponse: any = await this._user.getTermsByPaymentMethod(
        paymentMethodCustomerId
      );

      console.log('📄 T&C obtenido:', tacResponse);

      const tacHTML = tacResponse?.terms?.details || '<p>No se pudo cargar el contenido de los T&C</p>';
      const termsAndConditionId = tacResponse?.terms?._id;

      if (!termsAndConditionId) {
        throw new Error('No se pudo obtener el ID de los términos y condiciones');
      }

      // Mostrar modal con el HTML de los T&C
      Swal.fire({
        title: 'Términos y Condiciones de Domiciliación',
        html: `
          <div style="max-height: 400px; overflow-y: auto; text-align: left; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
            ${tacHTML}
          </div>
        `,
        width: '800px',
        showCancelButton: true,
        confirmButtonText: 'Aceptar T&C',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#dc3545'
      }).then((result) => {
        if (result.isConfirmed) {
          // Usar el nuevo método simplificado
          this.acceptTACSimple(paymentMethodCustomerId, termsAndConditionId);
        } else {
          Swal.fire({
            icon: 'info',
            title: 'T&C No Aceptados',
            text: 'Debe aceptar los términos y condiciones para continuar'
          });
        }
      });

    } catch (error) {
      console.error('❌ Error obteniendo T&C:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar el contenido de los T&C'
      });
    }
  }

  /**
   * Aceptar T&C con formato simplificado (nuevo formato del backend)
   */
  async acceptTACSimple(paymentMethodCustomerId: string, termsAndConditionId: string) {
    console.log('✅ Aceptando T&C:', { paymentMethodCustomerId, termsAndConditionId });

    Swal.fire({
      title: 'Aceptando T&C...',
      html: 'Por favor espere',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response: any = await this._user.acceptDirectDebitTerms(
        paymentMethodCustomerId,
        termsAndConditionId
      );

      console.log('✅ T&C aceptados:', response);

      Swal.fire({
        icon: 'success',
        title: '¡T&C Aceptados!',
        text: 'Los términos y condiciones han sido aceptados correctamente. La domiciliación está completa.',
        confirmButtonText: 'Continuar'
      }).then(() => {
        // Volver a validar para confirmar que ahora está domiciliado
        this.simulateInsurancePurchase();
      });

    } catch (error: any) {
      console.error('❌ Error aceptando T&C:', error);
      
      const errorMsg = error?.error?.msg || error?.message || 'Error al aceptar los términos y condiciones';
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg
      });
    }
  }

  /**
   * Aceptar T&C (método legacy - mantener por compatibilidad)
   * @deprecated Usar acceptTACSimple en su lugar
   */
  async acceptTAC(tacList: any[]) {
    console.log('⚠️ Método legacy acceptTAC llamado. Usar acceptTACSimple en su lugar.');
    console.log('✅ Aceptando T&C:', tacList);

    // Por ahora, redirigir al método nuevo si solo hay un T&C
    if (tacList && tacList.length === 1) {
      const tac = tacList[0];
      if (tac._id && tac.terms_and_condition) {
        return this.acceptTACSimple(tac._id, tac.terms_and_condition);
      }
    }

    Swal.fire({
      icon: 'warning',
      title: 'Método No Soportado',
      text: 'Este método de aceptación de T&C ya no está soportado. Por favor, use el flujo actualizado.'
    });
  }

  /**
   * Mostrar modal de autorización de cargo para copiar cuenta (CASO 2B)
   */
  showCopyAccountAuthorization(account: any, allAccounts: any[]) {
    console.log('🔄 Mostrando autorización de cargo para copiar cuenta:', account);
    
    // Obtener últimos 4 dígitos de la cuenta
    const last4Digits = account.account_number 
      ? account.account_number.slice(-4)
      : '****';
    
    Swal.fire({
      title: 'Autorización de Cargo en Cuenta',
      html: `
        <div style="text-align: left; padding: 20px;">
          <p style="font-size: 1.1em; margin-bottom: 15px; line-height: 1.6;">
            ¿Desea domiciliar el cobro de sus productos de <strong>Seguros</strong> en su cuenta del 
            <strong>${account.bank}</strong> finalizada en <strong>${last4Digits}</strong>?
          </p>
          
          <p style="font-size: 0.95em; color: #495057; margin-bottom: 20px;">
            Ahorra tiempo y libérate de preocupaciones, nosotros nos encargamos de realizar los cobros cuando corresponda.
          </p>
          
          <div style="background-color: #e7f3ff; padding: 15px; border-left: 4px solid #0d6efd; border-radius: 5px; margin-top: 15px;">
            <p style="margin: 0; font-size: 0.9em; color: #084298;">
              <i class="fas fa-info-circle"></i> 
              Al autorizar, deberá aceptar los términos y condiciones de domiciliación para los productos de <strong>Seguros</strong>.
            </p>
          </div>
          
          ${allAccounts.length > 1 ? `
            <div style="margin-top: 15px; font-size: 0.85em; color: #6c757d;">
              <p><i class="fas fa-credit-card"></i> También tiene ${allAccounts.length - 1} cuenta(s) adicional(es) disponible(s) para copiar.</p>
            </div>
          ` : ''}
        </div>
      `,
      icon: 'question',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-check"></i> Sí, deseo hacerlo',
      denyButtonText: '<i class="fas fa-bell"></i> Recordármelo más tarde',
      cancelButtonText: '<i class="fas fa-times"></i> No',
      confirmButtonColor: '#28a745',
      denyButtonColor: '#ff9800',
      cancelButtonColor: '#dc3545',
      width: '600px',
      customClass: {
        confirmButton: 'btn btn-success btn-lg',
        denyButton: 'btn btn-warning btn-lg',
        cancelButton: 'btn btn-danger btn-lg'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.copyAccountToProduct(account);
      } else if (result.isDenied) {
        // Usuario presiona "Recordármelo más tarde"
        this.createReminder();
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Operación Cancelada',
          text: 'No se copiará la cuenta. Puede registrar un nuevo método de pago si lo desea.',
          confirmButtonText: 'Entendido'
        });
      }
    });
  }

  /**
   * Copiar cuenta a otro producto
   */
  async copyAccountToProduct(account: any) {
    console.log('📋 Copiando cuenta a producto:', account);
    
    Swal.fire({
      title: 'Copiando Cuenta...',
      html: 'Por favor espere mientras se copia la cuenta',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Llamar al endpoint de copiar cuenta
      // El ID viene en account.id (desde other_domiciliations[0].id)
      const response: any = await this._user.copyPaymentMethodToProduct({
        payment_method_customer_id: account.id || account.payment_method_customer_id,
        target_product_type: this.productType
      });

      console.log('✅ Cuenta copiada exitosamente:', response);

      Swal.fire({
        icon: 'success',
        title: '¡Cuenta Copiada!',
        html: `
          <p>La cuenta del <strong>${account.bank}</strong> ha sido copiada exitosamente para <strong>${this.getProductName()}</strong></p>
          <p class="mt-3">Ahora debe aceptar los términos y condiciones de domiciliación.</p>
        `,
        confirmButtonText: 'Ver T&C'
      }).then(() => {
        // Cargar T&C pendientes
        this.loadPendingTAC();
      });

    } catch (error: any) {
      console.error('❌ Error copiando cuenta:', error);
      
      const errorMsg = error?.error?.msg || error?.message || 'Error al copiar la cuenta';
      
      Swal.fire({
        icon: 'error',
        title: 'Error al Copiar Cuenta',
        text: errorMsg,
        confirmButtonText: 'Entendido'
      });
    }
  }

  /**
   * Redirigir al módulo de registro de métodos de pago (CASO 3)
   */
  redirectToPaymentMethodRegistration() {
    console.log('🔄 Redirigiendo a registro de métodos de pago...');
    
    Swal.fire({
      icon: 'info',
      title: 'Redirección',
      html: `
        <p>En la aplicación móvil, el usuario sería redirigido a:</p>
        <p><strong>Módulo de Registro de Métodos de Pago</strong></p>
        <p>Donde podrá:</p>
        <ul style="text-align: left;">
          <li>Seleccionar un banco compatible</li>
          <li>Ingresar datos de su cuenta</li>
          <li>Aceptar términos y condiciones</li>
          <li>Activar domiciliación</li>
        </ul>
        <p><em>En esta consola, puede usar el módulo de Payment Methods existente</em></p>
      `,
      confirmButtonText: 'Entendido'
    });
  }

  /**
   * Reiniciar simulador
   */
  reset() {
    this.customerIdentification = '';
    this.customerNationality = 'V';
    this.customerData = null;
    this.validationResult = null;
    this.pendingTAC = null;
    this.currentStep = 'search';
    console.log('🔄 Simulador reiniciado');
  }

  /**
   * Obtener nombre del producto
   */
  getProductName(): string {
    const products: any = {
      'SEGUROS_MERCANTIL': 'Seguros Mercantil',
      'CREDIT': 'Crédito',
      'RECARGAS': 'Recargas'
    };
    return products[this.productType] || this.productType;
  }

  /**
   * Crear recordatorio de domiciliación
   */
  async createReminder() {
    if (!this.customerData) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No hay cliente seleccionado'
      });
      return;
    }

    console.log('🔔 Creando recordatorio para:', this.customerData.code);

    Swal.fire({
      title: 'Creando Recordatorio...',
      html: 'Por favor espere',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response: any = await this._user.createDomiciliationReminder({
        person_code: this.customerData.code,
        product_type: this.productType
      });

      console.log('✅ Recordatorio creado:', response);

      if (response.already_exists) {
        Swal.fire({
          icon: 'info',
          title: 'Recordatorio Ya Existe',
          html: `
            <p>Ya tienes un recordatorio programado para domiciliar tus pagos de <strong>${this.getProductName()}</strong></p>
            <p class="mt-3">Te notificaremos cuando sea el momento.</p>
          `,
          confirmButtonText: 'Entendido'
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: '¡Recordatorio Creado!',
          html: `
            <p>Te recordaremos mañana sobre la domiciliación de tus pagos de <strong>${this.getProductName()}</strong></p>
            <p class="mt-3"><i class="fas fa-bell"></i> Recibirás una notificación al abrir la app.</p>
          `,
          confirmButtonText: 'Perfecto'
        });
      }

    } catch (error: any) {
      console.error('❌ Error creando recordatorio:', error);
      
      const errorMsg = error?.error?.error || error?.message || 'Error al crear el recordatorio';
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg
      });
    }
  }

  /**
   * Obtener badge de estado
   */
  getStatusBadge(): { text: string, class: string } {
    switch (this.currentStep) {
      case 'search':
        return { text: 'Búsqueda', class: 'badge-secondary' };
      case 'validation':
        return { text: 'Validando...', class: 'badge-info' };
      case 'pending_tac':
        return { text: 'T&C Pendientes', class: 'badge-warning' };
      case 'register_payment':
        return { text: 'Registro Requerido', class: 'badge-danger' };
      case 'completed':
        return { text: 'Domiciliado', class: 'badge-success' };
      default:
        return { text: 'Desconocido', class: 'badge-secondary' };
    }
  }
}
